import React, {useState} from 'react';
import {X} from 'lucide-react';

interface ChangeRequest {
  id: string;
  context: string;
  description: string;
  currentSchema?: string;
  proposedSchema: string;
  priority: 'low' | 'medium' | 'high';
  status: 'pending' | 'in-progress' | 'completed';
}

const CHANGE_REQUESTS: ChangeRequest[] = [
  {
    id: 'free-email-domains',
    context: 'admin-settings',
    description: 'Add GraphQL mutations and queries for managing free email domains list',
    proposedSchema: `type Query {
  # Get the current list of free email domain categories
  <span class="text-red-600 font-semibold">getFreeEmailDomainCategories: [EmailDomainCategory!]!</span>
}

type Mutation {
  # Add a new domain to a specific category
  <span class="text-red-600 font-semibold">addFreeEmailDomain(</span>
  <span class="text-red-600 font-semibold">  categoryName: String!</span>
  <span class="text-red-600 font-semibold">  domain: String!</span>
  <span class="text-red-600 font-semibold">): EmailDomainCategory!</span>
  
  # Remove a domain from the list
  <span class="text-red-600 font-semibold">removeFreeEmailDomain(</span>
  <span class="text-red-600 font-semibold">  domain: String!</span>
  <span class="text-red-600 font-semibold">): Boolean!</span>
  
  # Reset to default list (based on HubSpot's list)
  <span class="text-red-600 font-semibold">resetFreeEmailDomains: [EmailDomainCategory!]!</span>
}

<span class="text-red-600 font-semibold">type EmailDomainCategory {</span>
<span class="text-red-600 font-semibold">  name: String!</span>
<span class="text-red-600 font-semibold">  domains: [String!]!</span>
<span class="text-red-600 font-semibold">}</span>

<span class="text-red-600 font-semibold"># Initial categories (based on HubSpot's blocked domains):</span>
<span class="text-red-600 font-semibold"># - Major Providers (gmail.com, yahoo.com, hotmail.com, etc.)</span>
<span class="text-red-600 font-semibold"># - Google/Microsoft/Yahoo/GMX Variants</span>
<span class="text-red-600 font-semibold"># - Asian Providers (qq.com, naver.com, etc.)</span>
<span class="text-red-600 font-semibold"># - European Providers (mail.ru, orange.fr, etc.)</span>
<span class="text-red-600 font-semibold"># - UK Providers (sky.com, btinternet.com, etc.)</span>
<span class="text-red-600 font-semibold"># - US ISP Providers (comcast.net, att.net, etc.)</span>
<span class="text-red-600 font-semibold"># - Other Providers</span>
<span class="text-red-600 font-semibold"># - Temporary/Disposable (mailinator.com, guerrillamail.com, etc.)</span>
<span class="text-red-600 font-semibold"># Total: 90+ domains across 11 categories</span>
<span class="text-red-600 font-semibold"># See: src/constants/freeEmailDomains.ts for complete list</span>`,
    priority: 'medium',
    status: 'pending',
  },
  {
    id: 'org-dates',
    context: 'organisations-detail',
    description: 'Add creation and last modified timestamps to Organisation',
    currentSchema: `type Organisation {
  active: Boolean!
  id: UUID!
  name: String!
  description: String
  numberOfUsers: Int!
  # ... other fields
}`,
    proposedSchema: `type Organisation {
  active: Boolean!
  id: UUID!
  name: String!
  description: String
  numberOfUsers: Int!
  <span class="text-red-600 font-semibold">createdAt: Long!</span>
  <span class="text-red-600 font-semibold">lastModified: Long!</span>
  # ... other fields
}`,
    priority: 'medium',
    status: 'pending',
  },
  {
    id: 'org-project-count',
    context: 'organisations-list',
    description: 'Add project count to organisations query (without requiring org context)',
    currentSchema: `type Organisation {
  active: Boolean!
  id: UUID!
  name: String!
  numberOfUsers: Int!
  numberOfArtworks: Int!
  stats: OrganisationStatistics!  # Requires org context
  # ... other fields
}

type OrganisationStatistics {
  project: ProjectStats!  # Not available in list query
}

type ProjectStats {
  projects: Int!  # Can't access this
}`,
    proposedSchema: `type Organisation {
  active: Boolean!
  id: UUID!
  name: String!
  numberOfUsers: Int!
  numberOfArtworks: Int!
  <span class="text-red-600 font-semibold">numberOfProjects: Int!  # Add direct field, no org context needed</span>
  stats: OrganisationStatistics!
  # ... other fields
}

<span class="text-red-600 font-semibold"># Make this field available in organisations list query</span>
<span class="text-red-600 font-semibold"># Similar to numberOfUsers and numberOfArtworks</span>`,
    priority: 'medium',
    status: 'pending',
  },
  {
    id: 'user-lastlogin-no-org',
    context: 'users-detail',
    description: 'Make lastLogin available without organization context',
    currentSchema: `type User {
  id: UUID!
  username: String!
  createdAt: Long!
  lastLogin: Long  # Currently requires org context
  # ... other fields
}`,
    proposedSchema: `type User {
  id: UUID!
  username: String!
  createdAt: Long!
  <span class="text-red-600 font-semibold">lastLogin: Long  # Should work without org context</span>
  # ... other fields
}

<span class="text-red-600 font-semibold"># Update resolver to not require organization selection</span>
<span class="text-red-600 font-semibold"># Error: "No organisation selected yet" should not occur</span>`,
    priority: 'high',
    status: 'pending',
  },
  {
    id: 'user-enable-disable',
    context: 'users-detail',
    description: 'Add mutations to enable/disable user accounts',
    proposedSchema: `type Mutation {
  # ... existing mutations
  
  <span class="text-red-600 font-semibold"># Admin only - Enable a user account</span>
  <span class="text-red-600 font-semibold">enableUser(username: String!): User!</span>
  
  <span class="text-red-600 font-semibold"># Admin only - Disable a user account</span>
  <span class="text-red-600 font-semibold">disableUser(username: String!): User!</span>
  
  <span class="text-red-600 font-semibold"># Or alternatively a single mutation:</span>
  <span class="text-red-600 font-semibold">setUserEnabled(username: String!, enabled: Boolean!): User!</span>
}`,
    priority: 'medium',
    status: 'pending',
  },
  {
    id: 'org-users-pagination',
    context: 'organisations-detail',
    description: 'Add pagination support for Organisation users field',
    currentSchema: `type Organisation {
  id: UUID!
  name: String!
  numberOfUsers: Int!
  users: [User!]!  # Currently limited, no pagination
  # ... other fields
}`,
    proposedSchema: `type Organisation {
  id: UUID!
  name: String!
  numberOfUsers: Int!  # Total count
  users: [User!]!  # Keep for backward compatibility (deprecated)
  
  <span class="text-red-600 font-semibold"># NEW: Paginated user list</span>
  <span class="text-red-600 font-semibold">usersPaginated(</span>
  <span class="text-red-600 font-semibold">  page: Int = 0</span>
  <span class="text-red-600 font-semibold">  pageSize: Int = 100</span>
  <span class="text-red-600 font-semibold">  sortBy: UserSortField</span>
  <span class="text-red-600 font-semibold">  sortOrder: SortOrder</span>
  <span class="text-red-600 font-semibold">): UserPage!</span>
  # ... other fields
}

<span class="text-red-600 font-semibold">type UserPage {</span>
<span class="text-red-600 font-semibold">  users: [User!]!</span>
<span class="text-red-600 font-semibold">  totalCount: Int!</span>
<span class="text-red-600 font-semibold">  page: Int!</span>
<span class="text-red-600 font-semibold">  pageSize: Int!</span>
<span class="text-red-600 font-semibold">  hasMore: Boolean!</span>
<span class="text-red-600 font-semibold">}</span>

<span class="text-red-600 font-semibold">enum UserSortField {</span>
<span class="text-red-600 font-semibold">  USERNAME</span>
<span class="text-red-600 font-semibold">  FIRST_NAME</span>
<span class="text-red-600 font-semibold">  LAST_NAME</span>
<span class="text-red-600 font-semibold">  EMAIL</span>
<span class="text-red-600 font-semibold">  CREATED_AT</span>
<span class="text-red-600 font-semibold">}</span>

<span class="text-red-600 font-semibold">enum SortOrder {</span>
<span class="text-red-600 font-semibold">  ASC</span>
<span class="text-red-600 font-semibold">  DESC</span>
<span class="text-red-600 font-semibold">}</span>

<span class="text-red-600 font-semibold"># Critical: Current users field is limited (showing 1 of 100+ for PPF Europe)</span>
<span class="text-red-600 font-semibold"># Admin dashboard needs to see all users for large organisations</span>`,
    priority: 'high',
    status: 'pending',
  },
  {
    id: 'user-search-filters',
    context: 'users-assignment',
    description: 'Add filtering parameters to findUsersInAnyOrganisation for enabled/acceptedTerms status',
    currentSchema: `type Query {
  # Current: Only accepts username parameter
  findUsersInAnyOrganisation(username: String): [User]!
}

# Problems:
# - Returns ALL users (disabled, unverified, inactive)
# - Client must filter after receiving data
# - Unnecessary data transfer for invalid users
# - Limited to 100 users max`,
    proposedSchema: `type Query {
  <span class="text-red-600 font-semibold"># Enhanced: Add status filtering</span>
  findUsersInAnyOrganisation(
    username: String
    <span class="text-red-600 font-semibold">enabled: Boolean        # Filter by enabled status</span>
    <span class="text-red-600 font-semibold">acceptedTerms: Boolean  # Filter by terms acceptance</span>
    <span class="text-red-600 font-semibold">limit: Int              # Max results (default: 100)</span>
  ): [User]!
}

<span class="text-red-600 font-semibold"># Use cases:</span>
<span class="text-red-600 font-semibold"># - User assignment: Only show valid users (enabled=true, acceptedTerms=true)</span>
<span class="text-red-600 font-semibold"># - Admin cleanup: Find disabled accounts (enabled=false)</span>
<span class="text-red-600 font-semibold"># - Onboarding: Find users pending terms (acceptedTerms=false)</span>

<span class="text-red-600 font-semibold"># Benefits:</span>
<span class="text-red-600 font-semibold"># - Reduced data transfer (don't send invalid users)</span>
<span class="text-red-600 font-semibold"># - Faster queries (database-level filtering)</span>
<span class="text-red-600 font-semibold"># - Better UX (cleaner search results)</span>
<span class="text-red-600 font-semibold"># - Backward compatible (all params optional)</span>`,
    priority: 'medium',
    status: 'pending',
  },
  {
    id: 'storage-history',
    context: 'organisations-detail',
    description: 'Add historical storage data for graphing trends over time',
    currentSchema: `type Query {
  # Current: Only returns current snapshot
  organisationMediaCenterStorageStorage(
    organisationId: UUID!
    unit: FolderStorageUnit
  ): OrganisationStorageSummary!
}

type OrganisationStorageSummary {
  organisationId: UUID!
  organisationName: String
  totalSize: BigDecimal!
  fullSize: BigDecimal!
  unit: FolderStorageUnit!
}`,
    proposedSchema: `type Query {
  # Existing query (keep for backward compatibility)
  organisationMediaCenterStorageStorage(
    organisationId: UUID!
    unit: FolderStorageUnit
  ): OrganisationStorageSummary!
  
  <span class="text-red-600 font-semibold"># NEW: Get historical storage data for graphing</span>
  <span class="text-red-600 font-semibold">organisationStorageHistory(</span>
  <span class="text-red-600 font-semibold">  organisationId: UUID!</span>
  <span class="text-red-600 font-semibold">  unit: FolderStorageUnit</span>
  <span class="text-red-600 font-semibold">  startDate: Long  # Unix timestamp</span>
  <span class="text-red-600 font-semibold">  endDate: Long    # Unix timestamp</span>
  <span class="text-red-600 font-semibold">): [StorageSnapshot!]!</span>
}

<span class="text-red-600 font-semibold"># NEW: Storage snapshot with timestamp</span>
<span class="text-red-600 font-semibold">type StorageSnapshot {</span>
<span class="text-red-600 font-semibold">  timestamp: Long!        # When measurement was taken</span>
<span class="text-red-600 font-semibold">  totalSize: BigDecimal!  # Compressed storage</span>
<span class="text-red-600 font-semibold">  fullSize: BigDecimal!   # Uncompressed size</span>
<span class="text-red-600 font-semibold">  unit: FolderStorageUnit!</span>
<span class="text-red-600 font-semibold">}</span>

<span class="text-red-600 font-semibold"># Enables time-series graphs showing storage growth</span>
<span class="text-red-600 font-semibold"># Backend should track daily/weekly snapshots</span>`,
    priority: 'high',
    status: 'pending',
  },
];

interface GraphQLChangeRequestsProps {
  isOpen: boolean;
  onClose: () => void;
  context?: string; // Optional context to filter requests
}

export function GraphQLChangeRequests({isOpen, onClose, context}: GraphQLChangeRequestsProps) {
  const [selectedRequest, setSelectedRequest] = useState<string | null>(null);

  if (!isOpen) return null;

  // Filter requests by context if provided
  const filteredRequests = context ? CHANGE_REQUESTS.filter((r) => r.context === context) : CHANGE_REQUESTS;

  const selectedRequestData = CHANGE_REQUESTS.find((r) => r.id === selectedRequest);

  const getPriorityColor = (priority: string) => {
    switch (priority) {
      case 'high':
        return 'bg-red-100 text-red-800 dark:bg-red-900/20 dark:text-red-400';
      case 'medium':
        return 'bg-orange-100 text-orange-800 dark:bg-orange-900/20 dark:text-orange-400';
      case 'low':
        return 'bg-blue-100 text-blue-800 dark:bg-blue-900/20 dark:text-blue-400';
      default:
        return 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-400';
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'completed':
        return 'bg-green-100 text-green-800 dark:bg-green-900/20 dark:text-green-400';
      case 'in-progress':
        return 'bg-blue-100 text-blue-800 dark:bg-blue-900/20 dark:text-blue-400';
      case 'pending':
        return 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-400';
      default:
        return 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-400';
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl w-full max-w-4xl max-h-[90vh] flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200 dark:border-gray-700">
          <div className="flex items-center space-x-3">
            {/* GraphQL Logo */}
            <svg className="w-8 h-8" viewBox="0 0 400 400" fill="none">
              <g>
                <path fill="#E10098" d="M57.468 302.66l-14.376-8.3 160.15-277.38 14.376 8.3z" />
                <path fill="#E10098" d="M39.8 272.2h320.3v16.6H39.8z" />
                <path fill="#E10098" d="M206.348 374.026l-160.21-92.5 8.3-14.376 160.21 92.5zM345.522 132.947l-160.21-92.5 8.3-14.376 160.21 92.5z" />
                <path fill="#E10098" d="M54.482 132.883l-8.3-14.375 160.21-92.5 8.3 14.376z" />
                <path
                  fill="#E10098"
                  d="M342.568 302.663l-160.15-277.38 14.376-8.3 160.15 277.38zM52.5 107.5h16.6v185H52.5zM330.9 107.5h16.6v185h-16.6z"
                />
                <path fill="#E10098" d="M203.522 367l-7.25-12.558 139.34-80.45 7.25 12.557z" />
                <path
                  fill="#E10098"
                  d="M369.5 297.9c-9.6 16.7-31 22.4-47.7 12.8-16.7-9.6-22.4-31-12.8-47.7 9.6-16.7 31-22.4 47.7-12.8 16.8 9.7 22.5 31 12.8 47.7M90.9 137c-9.6 16.7-31 22.4-47.7 12.8-16.7-9.6-22.4-31-12.8-47.7 9.6-16.7 31-22.4 47.7-12.8 16.7 9.7 22.4 31 12.8 47.7M30.5 297.9c-9.6-16.7-3.9-38 12.8-47.7 16.7-9.6 38-3.9 47.7 12.8 9.6 16.7 3.9 38-12.8 47.7-16.8 9.6-38.1 3.9-47.7-12.8M309.1 137c-9.6-16.7-3.9-38 12.8-47.7 16.7-9.6 38-3.9 47.7 12.8 9.6 16.7 3.9 38-12.8 47.7-16.7 9.6-38.1 3.9-47.7-12.8M200 395.8c-19.3 0-34.9-15.6-34.9-34.9 0-19.3 15.6-34.9 34.9-34.9 19.3 0 34.9 15.6 34.9 34.9 0 19.2-15.6 34.9-34.9 34.9M200 74c-19.3 0-34.9-15.6-34.9-34.9 0-19.3 15.6-34.9 34.9-34.9 19.3 0 34.9 15.6 34.9 34.9 0 19.3-15.6 34.9-34.9 34.9"
                />
              </g>
            </svg>
            <div>
              <h2 className="text-xl font-bold text-gray-900 dark:text-gray-100">GraphQL Schema Change Requests</h2>
              <p className="text-sm text-gray-600 dark:text-gray-400">Backend API enhancement requests</p>
            </div>
          </div>
          <button onClick={onClose} className="p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors">
            <X size={20} className="text-gray-600 dark:text-gray-400" />
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-6">
          {!selectedRequest ? (
            /* Request List */
            <div className="space-y-3">
              {filteredRequests.map((request) => (
                <button
                  key={request.id}
                  onClick={() => setSelectedRequest(request.id)}
                  className="w-full text-left bg-gray-50 dark:bg-gray-700/50 rounded-lg p-4 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors border border-gray-200 dark:border-gray-600"
                >
                  <div className="flex items-start justify-between mb-2">
                    <div className="flex-1">
                      <div className="flex items-center space-x-2 mb-1">
                        <span className="text-xs font-mono text-gray-500 dark:text-gray-400">{request.context}</span>
                        <span className={`px-2 py-0.5 rounded text-xs font-medium ${getPriorityColor(request.priority)}`}>{request.priority}</span>
                        <span className={`px-2 py-0.5 rounded text-xs font-medium ${getStatusColor(request.status)}`}>{request.status}</span>
                      </div>
                      <h3 className="text-sm font-semibold text-gray-900 dark:text-gray-100">{request.description}</h3>
                    </div>
                    <svg className="w-5 h-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                    </svg>
                  </div>
                </button>
              ))}

              {filteredRequests.length === 0 && (
                <div className="text-center py-12 text-gray-500 dark:text-gray-400">
                  <svg className="w-16 h-16 mx-auto mb-4 text-gray-300 dark:text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
                    />
                  </svg>
                  <p className="font-medium">No change requests yet</p>
                  <p className="text-sm mt-1">All GraphQL schema features are available</p>
                </div>
              )}
            </div>
          ) : (
            /* Request Detail */
            selectedRequestData && (
              <div className="space-y-6">
                <button
                  onClick={() => setSelectedRequest(null)}
                  className="flex items-center space-x-2 text-blue-600 dark:text-blue-400 hover:underline text-sm"
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                  </svg>
                  <span>Back to list</span>
                </button>

                <div>
                  <div className="flex items-center space-x-2 mb-2">
                    <span className="text-xs font-mono text-gray-500 dark:text-gray-400">{selectedRequestData.context}</span>
                    <span className={`px-2 py-0.5 rounded text-xs font-medium ${getPriorityColor(selectedRequestData.priority)}`}>
                      {selectedRequestData.priority}
                    </span>
                    <span className={`px-2 py-0.5 rounded text-xs font-medium ${getStatusColor(selectedRequestData.status)}`}>
                      {selectedRequestData.status}
                    </span>
                  </div>
                  <h3 className="text-lg font-bold text-gray-900 dark:text-gray-100 mb-2">{selectedRequestData.description}</h3>
                </div>

                {selectedRequestData.currentSchema && (
                  <div>
                    <h4 className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">Current Schema:</h4>
                    <pre className="bg-gray-900 text-gray-100 p-4 rounded-lg text-sm overflow-x-auto">
                      <code>{selectedRequestData.currentSchema}</code>
                    </pre>
                  </div>
                )}

                <div>
                  <h4 className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">
                    Proposed Schema: <span className="text-red-600 dark:text-red-400">(additions in red)</span>
                  </h4>
                  <pre className="bg-gray-900 text-gray-100 p-4 rounded-lg text-sm overflow-x-auto">
                    <code dangerouslySetInnerHTML={{__html: selectedRequestData.proposedSchema}} />
                  </pre>
                </div>

                <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-4">
                  <p className="text-sm text-blue-800 dark:text-blue-300">
                    <strong>Note:</strong> This change request should be communicated to the backend development team for implementation.
                  </p>
                </div>
              </div>
            )
          )}
        </div>

        {/* Footer */}
        <div className="p-6 border-t border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-700/50">
          <div className="flex items-center justify-between text-sm text-gray-600 dark:text-gray-400">
            <span>
              {filteredRequests.filter((r) => r.status === 'pending').length} pending •{' '}
              {filteredRequests.filter((r) => r.status === 'in-progress').length} in progress •{' '}
              {filteredRequests.filter((r) => r.status === 'completed').length} completed
            </span>
          </div>
        </div>
      </div>
    </div>
  );
}
